import { IElement, OneOfElements } from '@shared/models/project.model';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionName,
} from '../../element-quantity.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { isEqual } from 'lodash';

type ElementQuantityFallbackCountChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityFallbackCount];

/**
 * Applies pipeline/compute enrichment: sets the full {@link ExpressionValue} on
 * `element.quantity[name].fallbackCount`.
 *
 * @param element - Current element state
 * @param change - Quantity fallback payload
 * @returns Partial element with merged quantity record, or undefined if not applicable / no-op
 */
export const elementQuantityFallbackCountToChange = (
  element: OneOfElements,
  change: ElementQuantityFallbackCountChange,
): Partial<IElement> | undefined => {
  if (!projectGuards.is.element(element)) return undefined;

  const quantityRecord = getElementQuantityRecord(element);
  const row = quantityRecord[change.quantityName];
  // Use name-based check: isElementQuantityExpressionProperty uses getCount(requireCount=true)
  // which throws when a DB-loaded row lacks both count and fallbackCount.
  if (!row || !isElementQuantityExpressionName(row.name)) return undefined;

  if (isEqual(row.fallbackCount, change.fallbackCount)) return undefined;

  return {
    quantity: {
      ...quantityRecord,
      [change.quantityName]: {
        ...row,
        fallbackCount: change.fallbackCount,
      },
    },
  };
};
